import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { telegramClientApi, updateUserBalances } from '@/shared';
import { GetHypnowheelResponseType } from '@/entities';

interface HypnowheelState {
  data: GetHypnowheelResponseType | null;
  loading: boolean;
  error: string | null;
}

const initialState: HypnowheelState = {
  data: null,
  loading: false,
  error: null
};

export const fetchHypnowheel = createAsyncThunk<
  GetHypnowheelResponseType,
  void | { showLoader?: boolean },
  { rejectValue: string }
>('hypnowheel/fetchHypnowheel', async (_, { rejectWithValue }) => {
  try {
    const res = await telegramClientApi.getHypnowheel();
    if (res.error === null && res.data) return res.data;
    return rejectWithValue(res.error?.message || 'Failed to fetch hypnowheel data');
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const postSpinCompleted = createAsyncThunk<
  GetHypnowheelResponseType,
  { nonce: string; showLoader?: boolean },
  { rejectValue: string }
>('hypnowheel/postSpinCompleted', async ({ nonce }, { rejectWithValue, dispatch }) => {
  try {
    const res = await telegramClientApi.postSpinCompleted(nonce);
    if (res.error === null && res.data) {
      if (res.data.balances?.length) dispatch(updateUserBalances(res.data.balances));
      return res.data;
    }
    return rejectWithValue(res.error?.message || 'Failed to post spin completed');
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

const Slice = createSlice({
  name: 'hypnowheel',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHypnowheel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHypnowheel.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchHypnowheel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching hypnowheel';
      })
      .addCase(postSpinCompleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postSpinCompleted.fulfilled, (state) => {
        state.loading = false;
        state.data = null;
      })
      .addCase(postSpinCompleted.rejected, (state) => {
        state.loading = false;
      });
  }
});

export default Slice.reducer;
